import type React from "react";
import { useState, useCallback, useMemo } from "react";

import type { ApiCallbackParams } from "@similarweb/ai-kit";
import { FloatingFrame, FooterMode } from "@similarweb/ai-kit";
import { icons } from "@similarweb/ai-kit/icons";

import { translate } from "app/filters/i18nFilter";

import { AIAgentErrorMessage } from "./AIAgentErrorMessage";
import { ConfirmationModal } from "./ConfirmationModal";
import { STREAMING_STATUS } from "./constants";
import { default as SEOAnalysisForm } from "./SEOAnalysisForm";
import { FloatingFrameWrapper } from "./StyledComponents";
import { FileAttachmentTool } from "./tools/FileAttachmentTool";
import type { AIAgentFrameProps, StreamingStatusType } from "./types";
import { logErrorToSentry, logErrorToTrackers } from "./utils";

export const AIAgentFrame: React.FC<AIAgentFrameProps> = ({
    title,
    badge,
    actionButtonText,
    onActionButtonClick,
    onClose,
    apiCallback,
    isKeywordGroup,
    keyword,
    country,
    webSource,
    onDomainSelect,
    selectedDomain,
    customIcon,
    onMinimize,
    onRestore,
}) => {
    const [showForm, setShowForm] = useState<boolean>(true);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const [hasEnoughData, setHasEnoughData] = useState<boolean | null>(null);
    const [streamingStatus, setStreamingStatus] = useState<StreamingStatusType>(STREAMING_STATUS.INITIAL);
    const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

    // Memoized values
    const isAgentRunning = useMemo(() => streamingStatus === STREAMING_STATUS.STREAMING, [streamingStatus]);

    const headerConfig = useMemo(
        () => ({
            title,
            badge: badge || "",
            icon: customIcon || icons.contentAgentIcon,
            topButtons: {
                showMinimize: false,
                showClose: true,
                showHistory: false,
            },
        }),
        [title, badge, customIcon],
    );

    const handleActionButtonClick = useCallback(() => {
        setShowForm(false);
        onActionButtonClick();
    }, [onActionButtonClick]);

    const handleConfirmClose = useCallback(() => {
        setIsConfirmModalOpen(false);
        if (resolvePromise) {
            resolvePromise(true);
            setResolvePromise(null);
            onClose();
        } else {
            onClose();
        }
    }, [resolvePromise, onClose]);

    const handleMinimize = useCallback(
        (status: StreamingStatusType) => {
            onMinimize(status);
        },
        [onMinimize],
    );

    const handleRestore = useCallback(
        (status: StreamingStatusType) => {
            onRestore(status);
        },
        [onRestore],
    );

    const closeConfirmationCallback = useCallback((): Promise<boolean> => {
        if (streamingStatus === STREAMING_STATUS.STREAMING) {
            return new Promise((resolve) => {
                setResolvePromise(() => resolve);
                setIsConfirmModalOpen(true);
            });
        } else {
            return new Promise((resolve) => {
                setResolvePromise(() => resolve);
                onClose();
            });
        }
    }, [onClose, streamingStatus]);

    const handleCancelClose = useCallback(() => {
        setIsConfirmModalOpen(false);
        if (resolvePromise) {
            resolvePromise(false);
            setResolvePromise(null);
        }
    }, [resolvePromise]);

    const handleEventCallback = useCallback(
        (e: { type: string; data?: any }) => {
            switch (e.type) {
                case "close":
                    handleConfirmClose();
                    break;
                case "minimize":
                    handleMinimize(streamingStatus || STREAMING_STATUS.INITIAL);
                    break;
                case "restore":
                    handleRestore(streamingStatus || STREAMING_STATUS.INITIAL);
                    break;
                case "error":
                    logErrorToSentry("SEO Agent eventCallback error:", e?.data?.error);
                    logErrorToTrackers("EventCallback Error", e?.data?.error);
                    setShowErrorMessage(true);
                    setErrorCode(null);
                    setStreamingStatus(STREAMING_STATUS.IDLE);
                    break;
                case "handled_error":
                    logErrorToSentry("SEO Agent eventCallback content error:", e?.data?.error);
                    logErrorToTrackers("Content Error", e?.data?.error);
                    setErrorCode((e?.data?.message as string) || null);
                    setShowErrorMessage(true);
                    setStreamingStatus(STREAMING_STATUS.IDLE);
                    break;
                default:
                    // Handle any other event types if needed
                    break;
            }
        },
        [handleConfirmClose, handleMinimize, handleRestore, streamingStatus],
    );

    const handleApiCallback = useCallback(
        (params: ApiCallbackParams) => {
            return apiCallback(params);
        },
        [apiCallback],
    );

    const handleStreamingStatusChange = useCallback((status: any) => {
        setStreamingStatus(status);
    }, []);

    const handleDataSufficiencyCheck = useCallback((enoughData: boolean) => {
        setHasEnoughData(enoughData);
    }, []);

    return (
        <FloatingFrameWrapper>
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                isAgentRunning={isAgentRunning}
                onConfirm={handleConfirmClose}
                onCancel={handleCancelClose}
            />

            <FloatingFrame
                tooltipZIndex={1072}
                showThread={!showForm ? !showErrorMessage : undefined}
                config={{
                    closeConfirmationCallback,
                    headerProps: headerConfig,
                    footerProps: {
                        footerMode: streamingStatus === STREAMING_STATUS.IDLE ? FooterMode.None : FooterMode.Button,
                        buttonText: hasEnoughData ? actionButtonText : translate("ai.agent.seo.button.analyze.anyway"),
                        onClickHandler: handleActionButtonClick,
                        disabled: !selectedDomain,
                    },
                    apiCallback: handleApiCallback,
                    onStreamingStatusChange: handleStreamingStatusChange,
                    tools: [FileAttachmentTool],
                }}
                eventCallback={handleEventCallback}
            >
                {showForm ? (
                    <SEOAnalysisForm
                        isKeywordGroup={isKeywordGroup}
                        keyword={keyword}
                        country={country}
                        webSource={webSource}
                        onDomainSelect={onDomainSelect}
                        onDataSufficiencyCheck={handleDataSufficiencyCheck}
                    />
                ) : null}
                {showErrorMessage ? <AIAgentErrorMessage errorCode={errorCode} /> : null}
            </FloatingFrame>
        </FloatingFrameWrapper>
    );
};
